import { SITE_URL } from '@/lib/config';
import type { DocLocale } from './_registry';

/**
 * URL layout of the LLM-facing doc projections — fs-free on purpose, so the
 * client-side footer and the edge middleware can import it. The generators
 * that read MDX from disk live in `_llms.ts` and are build-time only.
 *
 *   /doc/<slug>.md       — one page's Slovak markdown (the primary language)
 *   /doc/en/<slug>.md    — the same page in English
 *   /llms.txt            — the index: one line per page, linking the .md URLs
 *   /llms-full.txt       — every Slovak page concatenated, in registry order
 *
 * Why the markdown lives on locale-less `/doc/…` paths rather than under
 * `/sk/doc/…`: the App Router refuses two different dynamic segment names at
 * one position, and `[locale]/doc/[slug]` already owns that position for the
 * HTML pages. A `.md` suffix on the HTML URL is what Anthropic's and Stripe's
 * docs serve, and the middleware maps an `Accept: text/markdown` request on
 * the HTML URL onto these paths, so the convention is honoured either way.
 */

export const LLMS_TXT_PATH = '/llms.txt';
export const LLMS_FULL_TXT_PATH = '/llms-full.txt';

/** Site-relative path of one page's markdown projection. */
export function docMarkdownPath(slug: string, locale: DocLocale): string {
  return locale === 'en' ? `/doc/en/${slug}.md` : `/doc/${slug}.md`;
}

/** Absolute URL of one page's markdown projection. */
export function docMarkdownUrl(slug: string, locale: DocLocale): string {
  return `${SITE_URL}${docMarkdownPath(slug, locale)}`;
}

/** Absolute URL of the rendered HTML page. */
export function docHtmlUrl(slug: string, locale: DocLocale): string {
  return `${SITE_URL}/${locale}/doc/${slug}`;
}

/** True when the client ranks `text/markdown` above `text/html` (RFC 9110 §12.5.1). */
export function prefersMarkdown(accept: string | null): boolean {
  if (!accept) return false;
  let markdown = -1;
  let html = -1;
  for (const part of accept.split(',')) {
    // Media types and the `q` parameter name are case-insensitive (RFC 9110
    // §8.3.1, §12.4.2); a malformed weight is read as the default 1, never as
    // NaN — NaN would poison Math.max and quietly hand the client HTML.
    const [type, ...params] = part.trim().toLowerCase().split(';');
    const qParam = params.map((x) => x.trim()).find((x) => x.startsWith('q='));
    const parsed = qParam ? Number.parseFloat(qParam.slice(2)) : 1;
    const q = Number.isNaN(parsed) ? 1 : parsed;
    if (type === 'text/markdown') markdown = Math.max(markdown, q);
    else if (type === 'text/html') html = Math.max(html, q);
  }
  return markdown > 0 && markdown >= html;
}
